import * as React from "react";
import { LoadingSpinner, Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";

interface PhysicalIndexStats {
  name: string;
  documentCount: number;
  storageMb: number;
  isShared: boolean;
}

interface SiteFacet {
  siteId: string;
  documentCount: number;
}

interface SharedIndexStats {
  indexName: string;
  totalDocuments: number;
  storageMb: number;
  sites: SiteFacet[];
}

interface IndexStatsResponse {
  sharedIndex: SharedIndexStats;
  allIndexes: PhysicalIndexStats[];
}

interface IndexTypeFacet {
  indexName: string;
  count: number;
}

interface SiteIndexBreakdown {
  siteId: string;
  totalDocuments: number;
  indexTypes: IndexTypeFacet[];
}

export default function SearchIndexes() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState<IndexStatsResponse | null>(null);
  const [expandedSite, setExpandedSite] = React.useState<string | null>(null);
  const [siteBreakdown, setSiteBreakdown] = React.useState<Record<string, SiteIndexBreakdown>>({});
  const [loadingSite, setLoadingSite] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<"shared" | "all">("shared");

  React.useEffect(() => {
    (async () => {
      try {
        const data: IndexStatsResponse = await (
          await api.admin.get("DatabaseTool/SearchIndexStats")
        ).json();
        setStats(data);
      } catch {
        displayToast(Severity.Error, "Failed to load search index stats");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function toggleSite(siteId: string) {
    if (expandedSite === siteId) {
      setExpandedSite(null);
      return;
    }
    setExpandedSite(siteId);
    if (siteBreakdown[siteId]) return;
    setLoadingSite(siteId);
    try {
      const data: SiteIndexBreakdown = await (
        await api.admin.get(`DatabaseTool/SearchIndexSite?siteId=${siteId}`)
      ).json();
      setSiteBreakdown(prev => ({ ...prev, [siteId]: data }));
    } catch {
      displayToast(Severity.Error, `Failed to load breakdown for site ${siteId}`);
    } finally {
      setLoadingSite(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  if (!stats) return <div className="p-6 text-red-600">Failed to load stats.</div>;

  const { sharedIndex, allIndexes } = stats;
  const legacyIndexes = allIndexes.filter(i => !i.isShared);
  const totalStorageMb = allIndexes.reduce((s, i) => s + i.storageMb, 0);
  const totalDocs = allIndexes.reduce((s, i) => s + i.documentCount, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* ── Summary Cards ─────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Total Physical Indexes"
          value={allIndexes.length.toString()}
        />
        <StatCard
          label="Total Documents"
          value={totalDocs.toLocaleString()}
        />
        <StatCard
          label="Total Storage"
          value={`${totalStorageMb.toFixed(1)} MB`}
        />
      </div>

      {/* ── Tabs ──────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <TabButton active={activeTab === "shared"} onClick={() => setActiveTab("shared")}>
          Shared Index ({sharedIndex.sites?.length ?? 0} customers)
        </TabButton>
        <TabButton active={activeTab === "all"} onClick={() => setActiveTab("all")}>
          All Physical Indexes ({allIndexes.length})
        </TabButton>
      </div>

      {activeTab === "shared" && (
        <SharedIndexPanel
          sharedIndex={sharedIndex}
          expandedSite={expandedSite}
          loadingSite={loadingSite}
          siteBreakdown={siteBreakdown}
          onToggleSite={toggleSite}
        />
      )}

      {activeTab === "all" && (
        <AllIndexesPanel allIndexes={allIndexes} legacyIndexes={legacyIndexes} />
      )}
    </div>
  );
}

// ── Shared Index Panel ─────────────────────────────────────────────────────────

function SharedIndexPanel({
  sharedIndex,
  expandedSite,
  loadingSite,
  siteBreakdown,
  onToggleSite
}: {
  sharedIndex: SharedIndexStats;
  expandedSite: string | null;
  loadingSite: string | null;
  siteBreakdown: Record<string, SiteIndexBreakdown>;
  onToggleSite: (siteId: string) => void;
}) {
  return (
    <div>
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200 flex gap-8">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Index Name</div>
          <div className="font-mono font-semibold">{sharedIndex.indexName}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total Documents</div>
          <div className="font-semibold">{sharedIndex.totalDocuments.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Storage</div>
          <div className="font-semibold">{sharedIndex.storageMb.toFixed(1)} MB</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Customers</div>
          <div className="font-semibold">{sharedIndex.sites?.length ?? 0}</div>
        </div>
      </div>

      {(!sharedIndex.sites || sharedIndex.sites.length === 0) ? (
        <div className="text-gray-500 p-4">No customers found in shared index.</div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-2 font-medium text-gray-600">Site ID</th>
              <th className="px-4 py-2 font-medium text-gray-600">Documents</th>
              <th className="px-4 py-2 font-medium text-gray-600">% of Total</th>
              <th className="px-4 py-2 font-medium text-gray-600">Index Types</th>
            </tr>
          </thead>
          <tbody>
            {sharedIndex.sites.map(site => {
              const pct = sharedIndex.totalDocuments > 0
                ? ((site.documentCount / sharedIndex.totalDocuments) * 100).toFixed(1)
                : "0";
              const isExpanded = expandedSite === site.siteId;
              const isLoading = loadingSite === site.siteId;
              const breakdown = siteBreakdown[site.siteId];

              return (
                <React.Fragment key={site.siteId}>
                  <tr
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => onToggleSite(site.siteId)}
                  >
                    <td className="px-4 py-2 font-mono font-semibold text-blue-700">
                      {isExpanded ? "▼" : "▶"} {site.siteId}
                    </td>
                    <td className="px-4 py-2">{site.documentCount.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 rounded-full h-1.5"
                            style={{ width: `${Math.min(parseFloat(pct), 100)}%` }}
                          />
                        </div>
                        <span className="text-gray-500 text-xs">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-400 text-xs">
                      {breakdown
                        ? breakdown.indexTypes.map(t => t.indexName).join(", ")
                        : isLoading ? "loading…" : "click to expand"}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50 border-t border-blue-100">
                      <td colSpan={4} className="px-8 py-3">
                        {isLoading ? (
                          <LoadingSpinner />
                        ) : breakdown ? (
                          <div className="flex flex-wrap gap-3">
                            {breakdown.indexTypes.map(t => (
                              <div key={t.indexName} className="bg-white rounded border border-blue-200 px-3 py-1.5 text-xs">
                                <span className="font-mono font-semibold text-blue-700">{t.indexName}</span>
                                <span className="ml-2 text-gray-500">{t.count.toLocaleString()} docs</span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── All Indexes Panel ──────────────────────────────────────────────────────────

function AllIndexesPanel({
  allIndexes,
  legacyIndexes
}: {
  allIndexes: PhysicalIndexStats[];
  legacyIndexes: PhysicalIndexStats[];
}) {
  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        {legacyIndexes.length} legacy individual indexes + 1 shared index.
        Legacy indexes can be deleted once their data is migrated to the shared index.
      </p>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="px-4 py-2 font-medium text-gray-600">Index Name</th>
            <th className="px-4 py-2 font-medium text-gray-600">Documents</th>
            <th className="px-4 py-2 font-medium text-gray-600">Storage (MB)</th>
            <th className="px-4 py-2 font-medium text-gray-600">Type</th>
          </tr>
        </thead>
        <tbody>
          {allIndexes.map(idx => (
            <tr key={idx.name} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-2 font-mono text-xs">
                {idx.isShared ? (
                  <span className="font-semibold text-blue-700">{idx.name}</span>
                ) : idx.name}
              </td>
              <td className="px-4 py-2">{idx.documentCount.toLocaleString()}</td>
              <td className="px-4 py-2">{idx.storageMb.toFixed(2)}</td>
              <td className="px-4 py-2">
                {idx.isShared ? (
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">shared</span>
                ) : (
                  <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded">legacy</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Small helpers ──────────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-blue-600 text-blue-700"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {children}
    </button>
  );
}
